import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import { PackageSearch, LayoutDashboard, FileSpreadsheet, Users, Route, BarChart3, Truck, LogOut, Menu, X, FileText, CalendarClock, KeyRound, Map, History, ShieldCheck, ClipboardList, GitCompare, Clock } from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', perm: null },
  { to: '/invoices', icon: FileSpreadsheet, label: 'Invoices', perm: 'invoices' },
  { to: '/tracking', icon: PackageSearch, label: 'Invoice Tracking', perm: 'tracking', feature: 'invoice_tracking' },
  { to: '/drivers', icon: Users, label: 'Drivers', perm: 'drivers' },
  { to: '/route-planner', icon: Route, label: 'Route Planner', perm: 'route_planner' },
  { to: '/live-map', icon: Map, label: 'Live Map', perm: 'live_map' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', perm: 'analytics', feature: 'analytics_dashboard' },
  { to: '/management-report', icon: ClipboardList, label: 'Management Report', perm: 'management_report', feature: 'analytics_dashboard' },
  { to: '/bc-reconciliation', icon: GitCompare, label: 'BC Reconciliation', perm: 'bc_reconciliation' },
  { to: '/driver-reports', icon: FileText, label: 'Driver Reports', perm: 'driver_reports', feature: 'driver_reports' },
  { to: '/odometer-report', icon: ClipboardList, label: 'Odometer Report', perm: 'odometer_report', feature: 'odometer_report' },
  { to: '/rescheduled', icon: CalendarClock, label: 'Rescheduled', perm: 'rescheduled' },
  { to: '/users', icon: ShieldCheck, label: 'User Management', perm: 'users', adminOnly: true },
  { to: '/activity', icon: Clock, label: 'Time on Portal', perm: 'activity', feature: 'portal_activity' },
];

// Filter nav items by the logged-in user's permissions
function canView(item, user) {
  if (item.adminOnly) return user.role === 'admin';
  if (!item.perm) return true; // Dashboard always visible
  if (user.role === 'admin' || user.permissions === 'ALL') return true;
  const perms = user.permissions || {};
  return perms[item.perm] && perms[item.perm].view === true;
}

export default function Layout() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const logout = () => { localStorage.clear(); navigate('/login'); };
  const [companyName, setCompanyName] = useState('');
  // Which features this company has. Null until loaded, so nothing is
  // hidden prematurely on first paint.
  const [features, setFeatures] = useState(null);
  useEffect(() => {
    fetch('/platform/admin/whoami')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.name) { setCompanyName(d.name); document.title = d.name; }
                   if (d && d.features) setFeatures(d.features); })
      .catch(() => {});
  }, []);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCur, setPwCur] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const submitPw = async () => {
    if (!pwCur || !pwNew) { toast.error('Fill in both passwords'); return; }
    if (pwNew.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwNew !== pwNew2) { toast.error('New passwords do not match'); return; }
    setPwBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: pwCur, new_password: pwNew });
      toast.success('Password changed');
      setPwOpen(false); setPwCur(''); setPwNew(''); setPwNew2('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change password');
    } finally { setPwBusy(false); }
  };

  return (
    <div style={{ display:'flex', height:'100vh', background:'#f8fafc', fontFamily:'Inter, sans-serif' }}>
      <div style={{ width: open ? 220 : 60, background:'#1e293b', transition:'width 0.2s', display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ padding:'16px', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid #334155' }}>
          <Truck size={22} color="#38bdf8" />
          {open && <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:14, lineHeight:1.2 }}>{companyName || 'Delivery Portal'}</span>}
          <button onClick={() => setOpen(!open)} style={{ marginLeft:'auto', background:'none', border:'none', color:'#94a3b8', cursor:'pointer' }}>
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
        <nav style={{ flex:1, padding:'8px', overflowY:'auto' }}>
          {navItems.filter(item => canView(item, user))
            .filter(item => !item.feature || !features || features[item.feature] !== false)
            .map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={to === '/'}
              style={({ isActive }) => ({
                display:'flex', alignItems:'center', gap:10, padding:'9px 10px',
                borderRadius:8, marginBottom:2, textDecoration:'none',
                background: isActive ? '#0f172a' : 'transparent',
                color: isActive ? '#38bdf8' : '#94a3b8', fontSize:13, fontWeight:500
              })}>
              <Icon size={18} />{open && label}
            </NavLink>
          ))}
        </nav>
        <div style={{ padding:'12px', borderTop:'1px solid #334155' }}>
          {open && <div style={{ color:'#64748b', fontSize:11, marginBottom:8 }}>{user.name || 'Admin'}</div>}
          <button onClick={() => setPwOpen(true)} style={{ display:'flex', alignItems:'center', gap:8, color:'#94a3b8', background:'none', border:'none', cursor:'pointer', fontSize:13, padding:'6px 4px' }}>
            <KeyRound size={16} />{open && 'Change password'}
          </button>
          <button onClick={logout} style={{ display:'flex', alignItems:'center', gap:8, color:'#94a3b8', background:'none', border:'none', cursor:'pointer', fontSize:13, padding:'6px 4px' }}>
            <LogOut size={16} />{open && 'Logout'}
          </button>
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', display:'flex', flexDirection:'column' }}>
        <Outlet />
      </div>

      {pwOpen && (
        <div onClick={() => !pwBusy && setPwOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:14, padding:24, width:'100%', maxWidth:400 }}>
            <h2 style={{ fontSize:17, fontWeight:700, color:'#1e293b', margin:'0 0 4px' }}>Change your password</h2>
            <div style={{ fontSize:13, color:'#64748b', marginBottom:18 }}>{user.email || user.name}</div>
            {[['Current password', pwCur, setPwCur],['New password', pwNew, setPwNew],['Confirm new password', pwNew2, setPwNew2]].map(([label, val, set]) => (
              <div key={label} style={{ marginBottom:13 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#475569', marginBottom:6 }}>{label}</label>
                <input type="password" value={val} onChange={e => set(e.target.value)} disabled={pwBusy}
                  style={{ width:'100%', padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:18 }}>
              <button onClick={() => setPwOpen(false)} disabled={pwBusy}
                style={{ padding:'10px 18px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>Cancel</button>
              <button onClick={submitPw} disabled={pwBusy}
                style={{ padding:'10px 18px', background:'#0284c7', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                {pwBusy ? 'Saving…' : 'Change password'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
